import { inject } from '@angular/core';
import { IContract } from '@dss-libs/portal/type/portal-types';
import { TTableColumn } from '@dss-libs/shared/type/common-types';
import { getRenderComponentType } from '@dss-libs/shared/utility/common-utilities';
import { TranslocoService } from '@jsverse/transloco';
import { TranslocoDatePipe, TranslocoDecimalPipe } from '@jsverse/transloco-locale';
import { OrderStatusCellComponent } from '../components/smart/orders/order-status-cell.component';

export const getOrdersTableColumns = (openOrderStatusModal: (orderId: string) => void): TTableColumn<IContract>[] => {
  const decimalPipe = inject(TranslocoDecimalPipe);
  const datePipe = inject(TranslocoDatePipe);
  const translate = inject(TranslocoService);
  return [
    {
      id: 'name',
      label: 'ORDER-DETAILS.ORDER-TITLE',
      minWidth: '16rem',
      tooltipFn: ({ name }) => name,
      renderFn: ({ name, orderNumber }) => {
        const orderNumberLabel = orderNumber ? decimalPipe.transform(orderNumber, { useGrouping: false }) : '';
        return `<div class="flex flex-col gap-1 overflow-hidden">
          <p class="font-medium text-sm text-ink truncate max-w-64">${name || '---'}</p>
          <p class="text-xs text-ink-muted tracking-[-0.012px] truncate">${orderNumberLabel}</p>
        </div>`;
      },
    },
    {
      id: 'recipients',
      minWidth: '12rem',
      label: 'ORDER-DETAILS.RECIPIENTS',
      renderFn: ({ recipients }) => {
        if (!recipients?.length) return '---';
        const names = recipients
          .slice(0, 2)
          .map((recipient) => recipient.name.trim().split(' ')[0])
          .join(', ');
        if (recipients.length <= 2) return names;
        const extraCount = decimalPipe.transform(recipients.length - 2, { useGrouping: false });
        return `${names}, +${extraCount} ${translate.translate('ORDER-DETAILS.MORE')}`;
      },
      tooltipFn: ({ recipients }) => recipients?.map((recipient) => recipient.name.trim()).join(', '),
    },
    {
      id: 'status',
      label: 'ORDER-DETAILS.STATUS',
      minWidth: '10rem',
      component: getRenderComponentType(OrderStatusCellComponent, (row: IContract) => ({
        status: row.status,
        completedCount: row.completedCount,
        recipientsCount: row.recipients?.length,
        onClick: () => openOrderStatusModal(row.id),
      })),
    },
    {
      id: 'lastChange',
      label: 'ORDER-DETAILS.LAST-CHANGE',
      minWidth: '15rem',
      renderFn: ({ lastChange }) =>
        lastChange ? datePipe.transform(lastChange, { dateStyle: 'medium', timeStyle: 'short' }) : '---',
    },
    {
      id: 'expiryDate',
      label: 'ORDER-DETAILS.EXPIRING-DATE',
      minWidth: '9rem',
      renderFn: ({ expiryDate }) => (expiryDate ? datePipe.transform(expiryDate, { dateStyle: 'medium' }) : '---'),
    },
  ];
};
